import { X } from 'lucide-react';
import { useEffect, useId, useRef } from 'react';
import type { ReactNode } from 'react';
import { cx } from '../utils/cx';
import { Button } from './Button';
import { IconButton } from './IconButton';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  size?: 'sm' | 'md';
  closeLabel?: string;
}

/**
 * Modale native (`<dialog>` + showModal) : piège de focus, touche Échap et arrière-plan inerte
 * fournis par le navigateur. L'élément portant `data-autofocus` reçoit le focus à l'ouverture.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  actions,
  size = 'sm',
  closeLabel = 'Fermer',
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      dialog.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      className={cx('av-dialog', size === 'md' && 'av-dialog--md')}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      {open ? (
        <div className="av-dialog__inner">
          <div className="av-dialog__header">
            <h2 id={titleId} className="av-dialog__title">
              {title}
            </h2>
            <IconButton label={closeLabel} onClick={onClose}>
              <X />
            </IconButton>
          </div>
          {description ? (
            <div id={descriptionId} className="av-dialog__description">
              {description}
            </div>
          ) : null}
          {children}
          {actions ? <div className="av-dialog__actions">{actions}</div> : null}
        </div>
      ) : null}
    </dialog>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: 'primary' | 'danger';
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation explicite d'une action sensible. Le focus initial est sur « Annuler » pour
 * qu'une validation au clavier par réflexe ne déclenche jamais l'action dangereuse.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Annuler',
  tone = 'danger',
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={title}
      description={description}
      actions={
        <>
          <Button variant="secondary" onClick={onCancel} data-autofocus="">
            {cancelLabel}
          </Button>
          <Button variant={tone} onClick={onConfirm} loading={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    />
  );
}
